import { useState, useContext, useRef, useEffect } from "react"
import _ from "lodash"
import { useQuery, useMutation } from "@apollo/client"
import { Link, useParams, useNavigate } from "react-router-dom"
import { LoaderMedium } from "../../components/Loaders"
import { Formik, Form } from "formik"
import { PickList } from "primereact/picklist"
import * as Yup from "yup"
import { trimTextValue } from "../../components/FieldTypes/helpers/trimTextValue"

// Comps
import HandleKioskSwitch from "./components/HandleKioskSwitch"
import KioskTemplateUsageNotice from "./components/KioskTemplateUsageNotice"

// Context
import { UserContext } from "../../context/userContext"

// Prime
import { RadioButton } from "primereact/radiobutton"
import { Messages } from "primereact/messages"
import { InputText as Input } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Button } from "primereact/button"

// Styles
import styles from "../../styles/Form.module.css"

// Queries
import {
  ADD_TEMPLATE,
  GET_TEMPLATE,
  GET_TEMPLATES_LIBRARY,
} from "./queries"
import useKioskTemplateUsageLock from "./helpers/useKioskTemplateUsageLock"

import "./PickList.css"

export default function CloneTemplate() {
  const [availability, setAvailability] = useState(true)
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const errMsg = useRef(null)
  const mutationMsgs = useRef(null)
  const [addTemplate, { data: mutationData }] = useMutation(ADD_TEMPLATE)
  const [loading, setLoading] = useState(true)

  const [sysRequiredBlocks, setSysRequiredBlocks] = useState([])
  const [source, setSource] = useState([])
  const [target, setTarget] = useState([])

  const onChange = (event) => {
    const newTarget = event.target

    // Get the current template type
    const templateType = data.currentTemplate.template_type

    // Define restricted blocks based on template type
    const blockedBlocks = {
      session_module: ["form", "activity"],
      data_collection_form: ["session", "activity"],
      activities_module: ["form", "session"],
    }

    // Ensure only one "session__assessment" block is allowed
    const assessmentBlocksInTarget = newTarget.filter((block) =>
      block.block_name.startsWith("session__assessment")
    )

    if (assessmentBlocksInTarget.length > 1) {
      return errMsg.current.show([
        {
          severity: "error",
          detail: "Only one 'Custom Assessment' block can be selected.",
        },
      ])
    }

    // Ensure system-required blocks are not removed
    const removedBlocks = _.difference(target, newTarget)
    const requiredRemoved = removedBlocks.filter((block) =>
      sysRequiredBlocks.includes(block.idBlock)
    )

    if (requiredRemoved.length > 0) {
      return errMsg.current.show([
        {
          severity: "error",
          detail: `Cannot remove system-required blocks: ${requiredRemoved
            .map((b) => b.block_name)
            .join(", ")}.`,
        },
      ])
    }

    // Check if any invalid blocks are being added
    const invalidBlocks = newTarget.filter((block) =>
      blockedBlocks[templateType]?.includes(block.block_name)
    )

    if (invalidBlocks.length > 0) {
      return errMsg.current.show([
        {
          severity: "error",
          detail: `The selected template type (${templateType}) does not allow the blocks: ${invalidBlocks
            .map((b) => b.block_name)
            .join(", ")}.`,
        },
      ])
    }

    // Update source and target lists if validation passes
    setSource(event.source)
    setTarget(newTarget)
  }

  const handleSubmit = async (values) => {
    const templateType = data.currentTemplate.template_type
    const blockedBlocks = {
      session_module: ["form", "activity"],
      data_collection_form: ["session", "activity"],
      activities_module: ["form", "session"],
    }

    // Check if there are any invalid blocks in the final list
    const invalidBlocks = target.filter((block) =>
      blockedBlocks[templateType]?.includes(block.block_name)
    )

    if (invalidBlocks.length > 0) {
      return mutationMsgs.current.show([
        {
          severity: "error",
          summary: "Error",
          detail: `The selected template type (${templateType}) cannot contain: ${invalidBlocks
            .map((b) => b.block_name)
            .join(", ")}. Please remove them before saving.`,
        },
      ])
    }

    // Proceed with mutation if validation passes
    await addTemplate({
      variables: {
        template_parent: idTemplate,
        organization_id: userCtx.session.user.organization_id,
        enterprise_id: userCtx.session.user.enterprise_id,
        created_by: userCtx.session.user.enterprise_id,
        updated_by: userCtx.session.user.enterprise_id,
        template_type: data.currentTemplate.template_type,
        blocks: JSON.stringify(
          target.map(({ idBlock, block_name, block_parent }) => ({
            idBlock,
            block_name,
            block_parent,
          }))
        ),
        ...values,
      },
      refetchQueries: [
        {
          query: GET_TEMPLATES_LIBRARY,
          variables: {
            staff_id: userCtx.session.user.staff_id,
            organization_id: userCtx.session.user.organization_id,
          },
        },
      ],
      onCompleted: () => {
        mutationMsgs.current.show([
          {
            severity: "success",
            summary: "Success",
            detail: "Changes saved.",
            sticky: true,
            closable: false,
          },
        ])
        setTimeout(() => navigate("/library/templates"), 1500)
      },
      onError: (error) => {
        mutationMsgs.current.show([
          {
            severity: "error",
            summary: "Error",
            detail: error.message,
            sticky: true,
            closable: false,
          },
        ])
      },
    })
  }

  const { idTemplate } = useParams()
  const isIntake = idTemplate === "intake_session"

  const { data } = useQuery(GET_TEMPLATE, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      idTemplate: idTemplate,
      organization_id: userCtx.session.user.organization_id,
      participant_id: "",
      type: "system",
    },
    skip: !userCtx,
    onCompleted: (data) => {
      let customBlocks = data.customBlocks || []
      const systemBlocks = data.systemBlocks || []
      const currentTemplateBlocks = data.currentTemplate.blocks || []

      // Custom assessment blocks
      const customAssessmentBlocks = _.filter(customBlocks, {
        block_name: "session__assessment____custom",
      })

      // Remove custom assessment block from custom blocks
      customBlocks = _.reject(customBlocks, {
        block_name: "session__assessment____custom",
      })

      // Add restriction to availability
      const restrictedTemplatesNames = ["intake_session", "discharge_session"]
      const restrictedTemplatesTypes = ["admin_module", "system_module"]
      const { template_name, template_type } = data.currentTemplate
      const isRestrictedTemplate =
        _.includes(restrictedTemplatesNames, template_name) ||
        _.includes(restrictedTemplatesTypes, template_type)

      if (isRestrictedTemplate) setAvailability(false)

      const mergeSystemBlockWithCustom = _.reduce(
        customBlocks,
        (acc, customBlock) => {
          const { block_name } = customBlock
          const removeDefaultBlock = _.reject(acc, { block_name })
          return [...removeDefaultBlock, customBlock]
        },
        systemBlocks
      )

      const mergeCurrentTemplateBlocksWithCustom = _.reduce(
        currentTemplateBlocks,
        (acc, block) => {
          const { block_name, index } = block
          let customBlock = _.find(customBlocks, { block_name })
          if (_.isEmpty(customBlock)) {
            return [...acc, block]
          }
          const customBlockWithIndex = { ...customBlock, index }
          return [...acc, customBlockWithIndex]
        },
        []
      )

      const availableBlocks = _.concat(
        _.differenceBy(
          mergeSystemBlockWithCustom,
          mergeCurrentTemplateBlocksWithCustom,
          "block_name"
        ),
        customAssessmentBlocks
      )

      const currentTemplateBlocksOrdered = _.orderBy(
        mergeCurrentTemplateBlocksWithCustom,
        "index"
      )

      const sysRequiredBlocksList = _.reduce(
        currentTemplateBlocks,
        (acc, block) => {
          const { system_required } = block.configuration
          if (!isIntake) {
            const skipRequiredList = [
              "participant__contact_information",
              "participant__demographics",
              "session__assessment____custom",
            ]
            const skipFromRequired = _.includes(skipRequiredList, block.idBlock)
            if (skipFromRequired) return acc
          }
          return system_required ? [...acc, block.idBlock] : acc
        },
        []
      )

      setSysRequiredBlocks(sysRequiredBlocksList)
      setSource(availableBlocks)
      setTarget(currentTemplateBlocksOrdered)
      setLoading(false)
    },
  })

  const {
    isToggleLockActive,
    toggleLockReason,
    kioskFormsUsingTemplate,
    noticeOrganizations,
    isKioskUsageCheckComplete,
  } = useKioskTemplateUsageLock({
    currentTemplate: data?.currentTemplate,
    userCtx,
  })

  // Check if allow_custom_config is true
  const allowCustomConfig = data?.currentTemplate?.allow_custom_config

  useEffect(() => {
    if (data && !allowCustomConfig) {
      // If the template does not allow custom config, redirect to library
      navigate("/library/templates")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (loading || !isKioskUsageCheckComplete) return <LoaderMedium />

  // also prevent rendering the rest of the component if redirecting
  if (data && !allowCustomConfig) {
    return null
  }

  const NewTemplate = Yup.object().shape({
    template_availability: Yup.number().nullable(),
    template_title: Yup.string(),
    template_description: Yup.string(),
    kiosk: Yup.number().nullable(),
  })

  const itemTemplate = (item) => {
    return (
      <div className="product-item">
        <div className="product-list-detail">
          <span className="material-icons material-symbols-outlined">
            summarize
          </span>
          <span className="product-category">{item.block_title}</span>
        </div>
      </div>
    )
  }

  const handleTemplateAvailability = (formik) => {
    if (!availability) return null
    return (
      <div className={styles.field}>
        <label className={styles.label}>Template availability</label>
        <div>
          <RadioButton
            inputId="template_enable"
            name="template_enable"
            value={1}
            checked={formik.values.template_availability === 1}
            disabled={isToggleLockActive}
            onChange={(e) =>
              formik.setFieldValue("template_availability", e.value)
            }
          />
          <label htmlFor="template_enable">
            Enable template for Organizations
          </label>
        </div>
        <div>
          <RadioButton
            inputId="template_disable"
            name="template_disable"
            value={0}
            checked={formik.values.template_availability === 0}
            disabled={isToggleLockActive}
            onChange={(e) =>
              formik.setFieldValue("template_availability", e.value)
            }
          />
          <label htmlFor="template_disable">Disable template</label>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1>Customize Template</h1>
      <Link
        to="/library/templates"
        style={{ marginBottom: 30, display: "inline-block" }}
        className="btn-sm"
      >
        Back to Library
      </Link>
      <Formik
        initialValues={{
          template_availability:
            data.currentTemplate.template_availability || 1,
          template_name: data.currentTemplate.template_name,
          template_description: data.currentTemplate.template_description,
          template_title: data.currentTemplate.template_title,
          kiosk: data.currentTemplate.kiosk,
        }}
        validationSchema={NewTemplate}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          return (
            <Form className={styles.form}>
              {handleTemplateAvailability(formik)}
              <HandleKioskSwitch disabled={isToggleLockActive} />
              <KioskTemplateUsageNotice
                isVisible={isToggleLockActive}
                lockReason={toggleLockReason}
                formsUsingTemplate={kioskFormsUsingTemplate}
                organizations={noticeOrganizations}
                includeTemplateAvailability={availability}
              />

              <div className={styles.field}>
                <label className={styles.label}>Current Template Name:</label>
                <Input
                  name="template_name"
                  value={data.currentTemplate.template_name}
                  disabled
                  className={styles.value}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>New Template Name:</label>
                <Input
                  name="template_title"
                  onChange={(e) => formik.handleChange(trimTextValue(e))}
                  onBlur={formik.handleBlur}
                  value={formik.values.template_title}
                  className={styles.value}
                />
                {formik.touched.template_title &&
                  formik.errors.template_title && (
                    <div className={styles.errFieldMsg}>
                      {formik.errors.template_title}
                    </div>
                  )}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Description:</label>
                <InputTextarea
                  name="template_description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.template_description}
                  className={styles.valueW75}
                />
                {formik.touched.template_description &&
                  formik.errors.template_description && (
                    <div className={styles.errFieldMsg}>
                      {formik.errors.template_description}
                    </div>
                  )}
              </div>
              <hr />
              <div style={{ width: "80%" }}>
                <p>
                  Administrators can use the selector below to modify and update
                  the existing application template. The Available Blocks are
                  based on the type of template (Activities, Forms, Sessions).
                  Select any blocks that should be added or removed from the
                  current template.
                </p>
                <br />
                <p>
                  Blocks can be sequentially ordered for template display. Any
                  updates made and saved to templates will be automatically
                  reflected in all application locations.
                </p>
                <div style={{ margin: "10px 0" }}>
                  <Messages ref={errMsg} />
                </div>
              </div>

              <div className="picklist" style={{ marginTop: 45, padding: 10 }}>
                <PickList
                  source={source}
                  target={target}
                  itemTemplate={itemTemplate}
                  sourceHeader="Available"
                  targetHeader="Selected"
                  sourceStyle={{ height: "342px" }}
                  targetStyle={{ height: "342px" }}
                  onChange={onChange}
                  style={{ fontSize: "small" }}
                  className={styles.value}
                />
              </div>
              <hr />
              <div style={{ height: 45, marginBottom: 45 }}>
                <Messages ref={mutationMsgs} />
                {!mutationData && (
                  <Button
                    label="Submit"
                    icon="pi pi-check"
                    loading={formik.isSubmitting}
                    disabled={!formik.isValid}
                    type="submit"
                    className={styles.submitButton}
                  />
                )}
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
